/**
 * Every destination a merchant can reach, with the gate that decides whether
 * THIS merchant can reach it — and the words their trade uses for it.
 *
 * UI_CONSOLIDATION_PLAN 4C + 4D. The command palette is the one surface that
 * lists the whole app at once, so it is also the one surface where a wrong gate
 * is immediately visible: an entry that jumps to a page the user cannot open is
 * a dead end with our name on it. The gate grammar is deliberately the SAME one
 * the hub containers use (`cap:` / `tool:` / `plugin:` / `surface:` / `area:` /
 * `fn:`, joined by ` && ` and ` || `), resolved with the SAME helpers the
 * Sidebar uses — so palette, sidebar and hub cannot disagree about what exists.
 *
 * 🔴 LABELS GO THROUGH `capWord`, NOT THROUGH STRING LITERALS (4D). A candle
 * brand reads "Delivery Notes" and "Recipes"; a mill reads "Delivery Challans"
 * and "Milling Yield"; fashion reads exactly what it read before, because
 * `FASHION_VOCAB` carries the old hardcoded words as its defaults.
 */
import {
  capWord, canAccessArea, hasCapability, hasSurface, isToolOn, managesStock,
  hasCatalogSchema, isOwner, canManageFeatures, word, isSiteOnly,
} from '../../utils/businessConfig';
import { isIndia } from '../../utils/currency';

// `fn:` tokens. Kept small on purpose — a gate that needs a function nobody
// else calls is usually a gate that belongs in the config instead.
const FNS = {
  isIndia,
  managesStock,
  hasCatalogSchema,
  noCatalogSchema: () => !hasCatalogSchema(),
  isOwner,
  canManageFeatures,
};

/** Resolve ONE gate token. Unknown kinds are FALSE — a typo hides an entry
 *  (someone reports it) rather than exposing one (nobody notices). */
const resolveToken = (token, ctx) => {
  const t = String(token).trim();
  if (!t || t === 'always') return true;
  const idx = t.indexOf(':');
  if (idx === -1) return false;
  const kind = t.slice(0, idx);
  const key = t.slice(idx + 1);
  switch (kind) {
    case 'cap': return hasCapability(key);
    case 'tool': return isToolOn(key);
    case 'plugin': return Boolean(ctx.isPluginEnabled && ctx.isPluginEnabled(key));
    case 'surface': return hasSurface(key);
    case 'area': return canAccessArea(key);
    case 'admin': return Boolean(ctx.isPlatformAdmin);
    case 'fn': return typeof FNS[key] === 'function' ? Boolean(FNS[key]()) : false;
    default: return false;
  }
};

export const resolveGate = (gate, ctx) =>
  String(gate == null ? 'always' : gate)
    .split('||')
    .some((orPart) => orPart.split('&&').every((tok) => resolveToken(tok, ctx)));

// ── The map ─────────────────────────────────────────────────────────────────
// `label` is a function so it re-reads the vocabulary every time the palette
// opens: switching a tool or renaming a word in Personalize must not need a
// reload to show up here.
const TARGETS = [
  // ── Today ────────────────────────────────────────────────────────────────
  { group: 'Today', label: () => 'Today', path: '/today', gate: 'always',
    keywords: 'home dashboard landing what needs me' },
  { group: 'Today', label: () => 'Day Summary', path: '/dashboard', gate: 'plugin:daily_ops',
    keywords: 'morning digest numbers daily ops' },

  // ── Sell ─────────────────────────────────────────────────────────────────
  { group: 'Sell', label: () => capWord('counter', 'Counter Billing'), path: '/counter',
    gate: 'cap:counter_billing', keywords: 'pos bill scan cash upi' },
  { group: 'Sell', label: () => 'Order Board', path: '/order-board?tab=board',
    gate: 'cap:pay_later_orders || cap:counter_billing', keywords: 'kanban kitchen tickets live' },
  { group: 'Sell', label: () => 'Pre-orders', path: '/order-board?tab=preorders',
    gate: 'cap:counter_billing', keywords: 'advance booking delivery day' },
  { group: 'Sell', label: () => 'Appointments', path: '/appointments',
    gate: 'cap:appointments', keywords: 'calendar booking slot salon clinic' },
  { group: 'Sell', label: () => `Website ${capWord('orders', 'Orders')}`, path: '/Orders',
    gate: 'surface:online_orders', keywords: 'web online orders shipping fulfil' },
  { group: 'Sell', label: () => `All ${capWord('orders', 'Orders')}`, path: '/orders-unified',
    gate: 'plugin:unified_inbox && surface:online_orders', keywords: 'inbox unified every order' },
  { group: 'Sell', label: () => 'Order Book', path: '/orderbook', gate: 'always',
    keywords: 'register offline sale record' },
  { group: 'Sell', label: () => 'WhatsApp', path: '/whatsapp', gate: 'plugin:whatsapp',
    keywords: 'chat messages' },
  { group: 'Sell', label: () => 'Custom Requests', path: '/Proposals', gate: 'plugin:bid_requests',
    keywords: 'custom order enquiry quote bid' },
  { group: 'Sell', label: () => 'Enquiries', path: '/messages', gate: 'surface:storefront_design',
    keywords: 'contact form messages' },
  { group: 'Sell', label: () => 'Subscriptions', path: '/subscriptions', gate: 'tool:subscriptions',
    keywords: 'recurring subscribe and save mandate' },

  // ── Catalog & stock ──────────────────────────────────────────────────────
  { group: 'Catalog', label: () => `Add ${capWord('product', 'Product')}`, path: '/UploadProduct',
    gate: 'always', keywords: 'add new item create upload' },
  { group: 'Catalog', label: () => `All ${capWord('products', 'Products')}`, path: '/catalog',
    gate: 'fn:hasCatalogSchema', keywords: 'items list catalog' },
  { group: 'Catalog', label: () => 'Catalog Settings', path: '/catalog-settings',
    gate: 'fn:noCatalogSchema', keywords: 'sizes fabric care upload defaults' },
  { group: 'Catalog', label: () => 'Categories', path: '/categories', gate: 'always',
    keywords: 'sections navigation menu' },
  { group: 'Catalog', label: () => 'Stock', path: '/inventory?tab=stock', gate: 'plugin:inventory',
    keywords: 'inventory quantity on hand' },
  { group: 'Catalog', label: () => 'Batches & Expiry', path: '/inventory?tab=batches',
    gate: 'cap:batch_expiry', keywords: 'lot fefo shelf life' },
  { group: 'Catalog', label: () => 'Serials & IMEI', path: '/inventory?tab=serials',
    gate: 'cap:serial_tracking', keywords: 'imei warranty unit' },
  { group: 'Catalog', label: () => 'Locations', path: '/inventory?tab=locations',
    gate: 'cap:multi_location', keywords: 'branch godown transfer' },
  { group: 'Catalog', label: () => 'Stock Take', path: '/inventory?tab=stocktake',
    gate: 'cap:stock_take', keywords: 'count physical reorder' },
  { group: 'Catalog', label: () => 'Stock Ageing', path: '/inventory?tab=ageing',
    gate: 'fn:managesStock', keywords: 'dead stock old slow moving' },
  { group: 'Catalog', label: () => capWord('bom', 'Recipes & Yield'), path: '/recipes',
    gate: 'cap:bom_yield', keywords: 'bom bill of materials production yield' },
  // Not run through capWord: "Item Recipes" is a distinct feature from the
  // production BOM above, and `Item ${bom}` reads as "Item Recipes & Yield".
  { group: 'Catalog', label: () => 'Recipes', path: '/item-recipes',
    gate: 'cap:bom_yield', keywords: 'ingredients auto deduct' },
  { group: 'Catalog', label: () => 'Barcode Labels', path: '/labels', gate: 'tool:labels',
    keywords: 'print sticker price tag' },
  { group: 'Catalog', label: () => 'Collections', path: '/merchandising?tab=collections',
    gate: 'surface:collections', keywords: 'curate group storefront' },
  { group: 'Catalog', label: () => 'Shop the Look', path: '/merchandising?tab=gallery',
    gate: 'surface:collections', keywords: 'gallery photo grid' },
  { group: 'Catalog', label: () => 'Product Finders', path: '/merchandising?tab=finders',
    gate: 'surface:collections', keywords: 'quiz recommend' },
  { group: 'Catalog', label: () => 'Coupons', path: '/discounts?tab=coupons',
    gate: 'surface:coupons', keywords: 'discount code promo' },
  { group: 'Catalog', label: () => 'Sales', path: '/discounts?tab=sales',
    gate: 'surface:coupons', keywords: 'markdown offer price drop' },

  // ── Customers ────────────────────────────────────────────────────────────
  { group: 'Customers', label: () => `${capWord('customer', 'Customer')}s`,
    path: '/customers?tab=customers', gate: 'plugin:customers', keywords: 'crm contacts buyers' },
  { group: 'Customers', label: () => 'Segments', path: '/customers?tab=segments',
    gate: 'plugin:customers', keywords: 'rfm groups audience' },
  { group: 'Customers', label: () => 'B2B Buyers', path: '/customers?tab=b2b',
    gate: 'cap:b2b_pricing', keywords: 'wholesale dealer credit limit gstin' },
  { group: 'Customers', label: () => 'Price Lists', path: '/customers?tab=price-lists',
    gate: 'cap:price_lists', keywords: 'wholesale retail rate' },
  { group: 'Customers', label: () => 'Leads', path: '/leads', gate: 'cap:crm_pipeline',
    keywords: 'pipeline enquiry deal follow up' },
  { group: 'Customers', label: () => 'Quotations', path: '/quotes', gate: 'cap:b2b_pipeline',
    keywords: 'quote proposal sales order' },
  { group: 'Customers', label: () => 'Loyalty', path: '/engagement?tab=loyalty',
    gate: 'tool:loyalty', keywords: 'points rewards' },
  { group: 'Customers', label: () => 'Gift Vouchers', path: '/engagement?tab=vouchers',
    gate: 'tool:vouchers', keywords: 'gift card redeem' },
  { group: 'Customers', label: () => 'Referrals', path: '/engagement?tab=referrals',
    gate: 'plugin:customers', keywords: 'refer a friend invite' },
  { group: 'Customers', label: () => 'WhatsApp Campaigns', path: '/campaigns?tab=whatsapp',
    gate: 'tool:campaigns', keywords: 'broadcast blast offer' },
  { group: 'Customers', label: () => 'Email Marketing', path: '/campaigns?tab=email',
    gate: 'plugin:email_marketing && surface:marketing && area:marketing',
    keywords: 'newsletter broadcast' },
  { group: 'Customers', label: () => 'Automations', path: '/campaigns?tab=automations',
    gate: 'plugin:automations', keywords: 'rules when this then that' },
  { group: 'Customers', label: () => 'Returns', path: '/returns', gate: 'surface:online_orders',
    keywords: 'return request exchange refund' },

  // ── Money ────────────────────────────────────────────────────────────────
  { group: 'Money', label: () => 'Payments & Dues', path: '/money?tab=overview',
    gate: 'area:money', keywords: 'receivables collect overview dues' },
  { group: 'Money', label: () => 'Payments', path: '/money?tab=payments',
    gate: 'area:money && plugin:payments', keywords: 'received upi match' },
  { group: 'Money', label: () => capWord('khata', 'Khata'), path: '/money?tab=khata',
    gate: 'area:money && cap:khata', keywords: 'udhaar credit ledger owes' },
  { group: 'Money', label: () => 'Earnings', path: '/money?tab=earnings',
    gate: 'area:money && surface:connect_payments', keywords: 'settlement payout razorpay' },
  { group: 'Money', label: () => 'Books', path: '/books?tab=statements',
    gate: 'area:money && cap:ca_export', keywords: 'trial balance p&l balance sheet ledger ca' },
  { group: 'Money', label: () => 'Day Book', path: '/books?tab=daybook',
    gate: 'area:money && cap:ca_export', keywords: 'daily entries cash' },
  { group: 'Money', label: () => 'Bank Reconciliation', path: '/books?tab=bankrec',
    gate: 'area:money && cap:ca_export', keywords: 'statement match bank' },
  { group: 'Money', label: () => 'Compliance', path: '/books?tab=compliance',
    gate: 'area:money && cap:ca_export', keywords: 'gstr3b e-invoice e-way period lock' },
  { group: 'Money', label: () => `${capWord('estimate', 'Estimate')}s`,
    path: '/billing-documents?tab=estimates', gate: 'tool:estimates', keywords: 'quote proforma' },
  { group: 'Money', label: () => `${capWord('challan', 'Delivery Challan')}s`,
    path: '/billing-documents?tab=challans', gate: 'tool:challans', keywords: 'dispatch goods note' },
  { group: 'Money', label: () => 'Credit Notes', path: '/billing-documents?tab=credit-notes',
    gate: 'tool:returns', keywords: 'refund return restock' },
  // The old Reminders page is the Money-In OVERVIEW tab now (aged receivables +
  // the cadence/UPI settings on its gear), so point straight at it rather than
  // through the `/reminders` redirect. `area:money` is in the gate because the
  // hub enforces it: without it a cashier would be offered a jump into a screen
  // that renders nothing for them.
  { group: 'Money', label: () => 'Payment Reminders', path: '/money?tab=overview',
    gate: 'area:money && tool:reminders', keywords: 'nudge chase dues whatsapp' },
  { group: 'Money', label: () => 'Expenses', path: '/expenses', gate: 'plugin:expenses',
    keywords: 'spend cost bills paid' },
  { group: 'Money', label: () => 'Suppliers', path: '/suppliers', gate: 'plugin:suppliers',
    keywords: 'vendor payable purchase ledger' },
  { group: 'Money', label: () => 'Purchases', path: '/purchases', gate: 'plugin:suppliers',
    keywords: 'purchase receive goods inward' },
  { group: 'Money', label: () => 'P&L Report', path: '/reports?tab=pnl', gate: 'plugin:report_pnl',
    keywords: 'profit loss margin' },
  { group: 'Money', label: () => 'GSTR-1', path: '/reports?tab=gstr1',
    gate: 'plugin:report_gstr1 && fn:isIndia', keywords: 'gst filing return b2b' },
  { group: 'Money', label: () => 'Goals', path: '/reports?tab=goals', gate: 'plugin:report_goals',
    keywords: 'target pacing month' },
  { group: 'Money', label: () => 'Commerce Reports', path: '/reports?tab=commerce',
    gate: 'tool:returns || tool:loyalty || tool:vouchers || tool:campaigns',
    keywords: 'returns loyalty voucher campaign report' },
  { group: 'Money', label: () => 'Payment Setup', path: '/connect-payments',
    gate: 'area:money && surface:connect_payments', keywords: 'razorpay keys accept online' },

  // ── Store & setup ────────────────────────────────────────────────────────
  { group: 'Store & Setup', label: () => 'Website', path: '/design',
    gate: 'surface:storefront_design', keywords: 'editor design theme visual' },
  { group: 'Store & Setup', label: () => 'Themes', path: '/TemplateThemes',
    gate: 'admin:1 && surface:storefront_design', keywords: 'template base design pick' },
  { group: 'Store & Setup', label: () => 'Pages & Content', path: '/store-content',
    gate: 'surface:storefront_design', keywords: 'about contact policy legal words' },
  { group: 'Store & Setup', label: () => 'SEO', path: '/seo-settings',
    gate: 'surface:storefront_design', keywords: 'google meta title sitemap' },
  { group: 'Store & Setup', label: () => 'Analytics', path: '/analytics', gate: 'surface:analytics',
    keywords: 'visits funnel traffic' },
  { group: 'Store & Setup', label: () => 'Production', path: '/production',
    gate: 'plugin:production_board', keywords: 'work in progress stitching ready' },
  { group: 'Store & Setup', label: () => `${capWord('worker', 'Artisan')}s`, path: '/karigars',
    gate: 'plugin:team', keywords: 'karigar tailor staff roster rate' },
  { group: 'Store & Setup', label: () => 'Brand Profile', path: '/Profile', gate: 'always',
    keywords: 'logo gstin address business details' },
  { group: 'Store & Setup', label: () => 'Portfolio', path: '/portfolio', gate: 'cap:portfolio',
    keywords: 'link hub bio public page' },
  { group: 'Store & Setup', label: () => 'Store Settings', path: '/store-settings',
    gate: 'surface:storefront_design && area:settings',
    keywords: 'shipping cod returns welcome coupon' },
  { group: 'Store & Setup', label: () => 'Features', path: '/tools',
    gate: 'fn:canManageFeatures', keywords: 'switch on plugins capabilities modules' },
  { group: 'Store & Setup', label: () => 'Approvals', path: '/approvals', gate: 'cap:approvals',
    keywords: 'limits spend discount permission' },
  { group: 'Store & Setup', label: () => 'Documents', path: '/documents',
    gate: 'cap:documents && area:settings', keywords: 'licence registration expiry papers' },
  { group: 'Store & Setup', label: () => 'Team & Roles', path: '/team',
    gate: 'cap:staff_logins && fn:isOwner', keywords: 'staff login permission cashier' },
  { group: 'Store & Setup', label: () => 'Support', path: '/Contact', gate: 'always',
    keywords: 'help contact us problem' },
];

/**
 * The destinations THIS person can actually open, labelled in their own words.
 * `ctx` = { isPluginEnabled, isPlatformAdmin }.
 */
// A site-only (portfolio) account's whole portal is its website — the palette
// offers exactly the compact sidebar's destinations, nothing store-shaped.
const SITE_ONLY_PATHS = new Set([
  '/today', '/design', '/portfolio', '/seo-settings', '/analytics',
  '/Profile', '/subscription', '/Contact',
]);

export const buildNavTargets = (ctx = {}) =>
  TARGETS.filter((t) => resolveGate(t.gate, ctx))
    .filter((t) => !isSiteOnly() || SITE_ONLY_PATHS.has(t.path))
    .map((t) => ({
      group: t.group,
      label: t.label(),
      path: t.path,
      keywords: t.keywords || '',
    }));

/** The trade's plural noun for a sale — the palette's own result headings use
 *  it, so a café searches its "tickets" and a salon its "bookings". */
export const ordersWord = () => word('orders', 'orders');

export default buildNavTargets;
